import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import Installation from "@/pages/installation";
import Repair from "@/pages/repair";
import Maintenance from "@/pages/maintenance";
import Quote from "@/pages/quote";
import Contact from "@/pages/contact";
import Blog from "@/pages/blog";
import BlogPostPage from "@/pages/blog-post";
import HVAC from "@/pages/hvac";
import HVACVentilation from "@/pages/hvac-ventilation";
import HVACHeating from "@/pages/hvac-heating";
import HVACDuctwork from "@/pages/hvac-ductwork";
import HVACControls from "@/pages/hvac-controls";
import AdminLogin from "@/pages/admin-login";
import AdminDashboard from "@/pages/admin-dashboard";
import AdminEngineerPPM from "@/pages/admin-engineer-ppm";
import AdminEngineerJobs from "@/pages/admin-engineer-jobs";
import AdminSites from "@/pages/admin-sites";
import EngineerPortal from "@/pages/engineer-portal";
import EngineerPPM from "@/pages/engineer-ppm";
import EngineerJobs from "@/pages/engineer-jobs";
import EngineerJobDetail from "@/pages/engineer-job-detail";
import EngineerInspectionForm from "@/pages/engineer-inspection-form";
import Login from "@/pages/login";
import Register from "@/pages/register";
import Portal from "@/pages/portal";
import SaunaServicePage from "@/pages/sauna-service";
import SaunaSeoPage from "@/pages/sauna-seo-page";
import { saunaSeoPages, saunaArticles, saunaGuideIndex } from "@/data/sauna-seo";
import CaseStudiesPage, { CaseStudyPage } from "@/pages/case-studies";
import DynamicPage from "@/pages/dynamic-page";
import { useAnalytics } from "@/hooks/use-analytics";
import ChatWidget from "@/components/chat-widget";
import { useLocation } from "wouter";
import { ImageProtection } from "@/components/image-protection";

const queryClient = new QueryClient();

function ChatGate() {
  const [loc] = useLocation();
  if (loc.startsWith("/admin") || loc.startsWith("/portal") || loc.startsWith("/engineer")) return null;
  return <ChatWidget />;
}

function AppRoutes() {
  useAnalytics();

  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/installation" component={Installation} />
      <Route path="/repair" component={Repair} />
      <Route path="/maintenance" component={Maintenance} />
      <Route path="/quote" component={Quote} />
      <Route path="/contact" component={Contact} />
      <Route path="/blog" component={Blog} />
      <Route path="/blog/:slug">{params => <BlogPostPage slug={params.slug} />}</Route>
      <Route path="/case-studies" component={CaseStudiesPage} />
      <Route path="/case-studies/:slug">{params => <CaseStudyPage slug={params.slug} />}</Route>
      <Route path="/hvac" component={HVAC} />
      <Route path="/hvac/ventilation" component={HVACVentilation} />
      <Route path="/hvac/heating" component={HVACHeating} />
      <Route path="/hvac/ductwork" component={HVACDuctwork} />
      <Route path="/hvac/controls" component={HVACControls} />
      <Route path="/sauna/advise-design"><SaunaServicePage slug="advise-design" /></Route>
      <Route path="/sauna/supply"><SaunaServicePage slug="supply" /></Route>
      {[...saunaSeoPages, saunaGuideIndex, ...saunaArticles.filter(p => p.status === "published")].map(page => (
        <Route key={page.path} path={page.path}><SaunaSeoPage path={page.path} /></Route>
      ))}
      <Route path="/sauna/maintenance-repair"><SaunaServicePage slug="maintenance-repair" /></Route>
      <Route path="/sauna/:localSlug">{params => <SaunaSeoPage path={`/sauna/${params.localSlug}`} />}</Route>
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/portal" component={Portal} />
      <Route path="/admin" component={AdminLogin} />
      <Route path="/admin/dashboard" component={AdminDashboard} />
      <Route path="/admin/analytics" component={AdminDashboard} />
      <Route path="/admin/engineer-ppm" component={AdminEngineerPPM} />
      <Route path="/admin/engineer-jobs" component={AdminEngineerJobs} />
      <Route path="/admin/sites" component={AdminSites} />
      <Route path="/engineer" component={EngineerPortal} />
      <Route path="/engineer/ppm" component={EngineerPPM} />
      <Route path="/engineer/callouts">
         <EngineerJobs kind="callout" />
      </Route>
      <Route path="/engineer/site-surveys">
         <EngineerJobs kind="site_survey" />
      </Route>
      <Route path="/engineer/jobs/:id" component={EngineerJobDetail} />
      <Route path="/engineer/inspection/:id" component={EngineerInspectionForm} />
      <Route path="/:parent/:slug" component={DynamicPage} />
      <Route path="/:slug" component={DynamicPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <ImageProtection />
          <AppRoutes />
          <ChatGate />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
